import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DictationInputMode } from '../types/auth';

const STORAGE_KEY = 'pt-dictation-input-mode';

/**
 * Shared hook for reading/toggling dictation input mode preference.
 * Reads from user profile first, falls back to localStorage, defaults to 'keyboard'.
 */
export function useDictationInputMode() {
  const { user } = useAuth();

  const [mode, setMode] = useState<DictationInputMode>(() => {
    return (
      user?.dictationInputMode ||
      (localStorage.getItem(STORAGE_KEY) as DictationInputMode) ||
      'keyboard'
    );
  });

  const isLetterPicker = mode === 'letter-picker';

  const toggle = useCallback(() => {
    setMode((prev) => {
      const next: DictationInputMode = prev === 'letter-picker' ? 'keyboard' : 'letter-picker';
      localStorage.setItem(STORAGE_KEY, next);
      return next;
    });
  }, []);

  const setModeAndPersist = useCallback((newMode: DictationInputMode) => {
    setMode(newMode);
    localStorage.setItem(STORAGE_KEY, newMode);
  }, []);

  return { mode, isLetterPicker, toggle, setMode: setModeAndPersist };
}
